import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import CommunityReports from "./CommunityReports";

const API_BASE = `${process.env.REACT_APP_API_URL || "https://api.mioprice.com"}/api/healthcare`;
const PAGE_SIZE = 50;

// ── Constants ─────────────────────────────────────────────────────────────────
// Search terms loaded dynamically from /api/search-terms
// This array is a fallback only — populated at runtime
const QUICK_SEARCHES_FALLBACK = [
  "Knee MRI", "Colonoscopy", "Hip Replacement", "Blood Work",
  "Mammogram", "CT Scan", "Chest X-Ray", "EKG", "Echocardiogram",
  "Annual Physical", "Office Visit", "Therapy", "Sleep Study",
];


const BODY_AREAS = [
  { key: "joints", icon: "🦴", label: "Joints",     procedures: ["Knee MRI", "Hip replacement", "Knee replacement", "ACL repair", "Shoulder MRI", "Rotator cuff"] },
  { key: "heart",  icon: "❤️", label: "Heart",      procedures: ["EKG", "Echocardiogram", "Stress test", "Cardiac catheterization"] },
  { key: "blood",  icon: "🩸", label: "Blood",      procedures: ["Blood work", "CBC", "Lipid panel", "A1c", "Thyroid test", "Vitamin D", "PSA test"] },
  { key: "head",   icon: "🧠", label: "Head",       procedures: ["Brain MRI", "Head CT", "EEG"] },
  { key: "chest",  icon: "🫁", label: "Chest",      procedures: ["Chest X-ray", "Chest CT", "PET scan", "Ultrasound"] },
  { key: "digest", icon: "🫃", label: "Digestive",  procedures: ["Colonoscopy", "Endoscopy", "Gallbladder removal", "Abdominal ultrasound"] },
  { key: "womens", icon: "🌸", label: "Women's",    procedures: ["Mammogram", "Pap smear", "Pelvic ultrasound", "Bone density", "Colposcopy"] },
  { key: "mental", icon: "💬", label: "Mental",     procedures: ["Therapy", "Psychiatry"] },
  { key: "sleep",  icon: "💤", label: "Sleep",      procedures: ["Sleep study"] },
  { key: "eyes",   icon: "👁️", label: "Eyes",       procedures: ["Cataract surgery"] },
];

const PROCEDURE_AREA = {};
BODY_AREAS.forEach(area => {
  area.procedures.forEach(proc => {
    PROCEDURE_AREA[proc.toLowerCase()] = { icon: area.icon, label: area.label };
  });
});

const RADIUS_OPTIONS = [5, 10, 15, 20, 30, 50];

const TABS = [
  { key: "Facility",             label: "Hospitals & Facilities",  short: "Hospitals"  },
  { key: "Individual Providers", label: "Doctors & Clinicians",    short: "Providers"  },
  { key: "Ancillary",            label: "Labs & Medical Services",  short: "Labs"       },
  { key: "Behavioral Health",    label: "Behavioral Health",       short: "Behavioral" },
  { key: "all",                  label: "All",                     short: "All"        },
];

const SORT_OPTIONS = [
  { key: "price", label: "Lowest price"    },
  { key: "type",  label: "Hospitals first" },
];

const SPECIALTY_KEYWORDS = {
  hearing:  ["hearing","audiology","audiologist"],
  dental:   ["dental","dentist","orthodont"],
  vision:   ["vision","ophthalmol","optometry","optometrist"],
  podiatry: ["podiatry","podiatrist","foot care"],
  optical:  ["optical","optics","eyecare","eye care"],
};

const VALUE_CONFIG = {
  excellent: { label: "Excellent value", color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
  good:      { label: "Good value",      color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
  fair:      { label: "Fair value",      color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
};

const PAYER_COLORS = {
  uhc:    { bg: "#eff6ff", color: "#1d4ed8", label: "UHC"    },
  cigna:  { bg: "#f0fdf4", color: "#15803d", label: "Cigna"  },
  aetna:  { bg: "#fdf4ff", color: "#7e22ce", label: "Aetna"  },
  kaiser: { bg: "#fff7ed", color: "#c2410c", label: "Kaiser" },
};

const PIPELINE_STEPS = [
  { id: "zip",   label: "Locating area"     },
  { id: "cpt",   label: "Identifying procedure" },
  { id: "npi",   label: "Finding providers" },
  { id: "rates", label: "Querying rates"    },
  { id: "rank",  label: "Ranking results"   },
];

const fmt = (n) => n != null ? "$" + Math.round(n).toLocaleString() : "—";
const priceRange = (lo, hi) => (lo === hi ? fmt(lo) : `${fmt(lo)}–${fmt(hi)}`);

// Rate-type rule (validated across UHC, Aetna, Cigna, BCBS, Kaiser):
// show only real agreed dollar amounts. Exclude 'derived' (claims-estimate
// outliers), 'percentage' (% not $), 'per diem' (per-day inpatient).
// rateTypeOf tolerates either field name and defaults to "negotiated" so rows
// that don't yet carry the field (pre-pipeline-rebuild) are still shown.
const DISPLAY_TYPES = new Set(["negotiated", "fee schedule"]);
const rateTypeOf = (r) => (r.negotiated_type ?? r.rate_type ?? "negotiated");
const isDisplayable = (r) => DISPLAY_TYPES.has(rateTypeOf(r));

const lowestByClass = (rates, cls) => {
  const xs = (rates || [])
    .filter(r => r.billing_class === cls)
    .filter(isDisplayable)
    .map(r => r.min_rate);
  return xs.length ? Math.min(...xs) : null;
};
const rangeByClass = (rates, cls) => {
  const xs = (rates || [])
    .filter(r => r.billing_class === cls)
    .filter(isDisplayable)
    .flatMap(r => [r.min_rate, r.max_rate])
    .filter(v => v != null);
  return xs.length ? { lo: Math.min(...xs), hi: Math.max(...xs) } : null;
};
const headlinePrice = (r) =>
  lowestByClass(r.rates, "professional") ??
  lowestByClass(r.rates, "institutional") ??
  r.negotiated_rate ?? Infinity;

const getValueIndicators = (result, allResults) => {
  const inds = [];
  const prices = allResults.map(headlinePrice).filter(Number.isFinite);
  const minP = prices.length ? Math.min(...prices) : Infinity;
  const hp = headlinePrice(result);
  if (Number.isFinite(hp) && hp <= minP + 5)
    inds.push({ icon: "💰", label: "Best price", color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" });
  if ((result.google_rating || 0) >= 4.0 && (result.google_reviews || 0) >= 200)
    inds.push({ icon: "⭐", label: String(result.google_rating) + " rating", color: "#92400e", bg: "#fffbeb", border: "#fde68a" });
  if ((result.cms_star_rating || 0) >= 4)
    inds.push({ icon: "🏆", label: "CMS " + result.cms_star_rating + "★", color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" });
  if (Number.isFinite(hp) && hp <= minP * 1.3 && (result.google_rating || 0) >= 4.0 && (result.cms_star_rating || 0) >= 4)
    inds.push({ icon: "✅", label: "Best value", color: "#059669", bg: "#ecfdf5", border: "#6ee7b7" });
  return inds;
};

const groupRates = (rates) => {
  const byLoc = {};
  for (const r of (rates || [])) (byLoc[r.service_code] ||= []).push(r);
  const order = (c) => (c === "11" ? 0 : c === "all_settings" ? 2 : 1);
  return Object.entries(byLoc).sort(([a], [b]) => order(a) - order(b));
};

const SERVICE_LABELS = {
  "11": "At an office", "21": "Inpatient", "22": "Hospital outpatient", "23": "Emergency room",
  "24": "Surgery center", "19": "Off-campus outpatient", "81": "Lab", all_settings: "All settings",
};
const serviceLabel = (code) => SERVICE_LABELS[code] || code;
const FEE_LABELS = { professional: "Physician fee", institutional: "Facility fee" };
const feeLabel = (cls) => FEE_LABELS[cls] || cls;

// Title case
const ACRONYMS = new Set(["ssm","osf","uic","ui","rml","amita","bmc","umc","inc","llc","er","icu","nicu","picu","va","asc","ii","iii","iv","pc","sc","usa"]);
const SMALL    = new Set(["of","and","the","at","for","in","on","to","a","an"]);
const capWord  = (w) => { const wl = w.toLowerCase(); if (!wl) return w; if (wl.startsWith("mc") && wl.length > 2) return "Mc" + wl[2].toUpperCase() + wl.slice(3); if (wl.startsWith("mac") && wl.length > 4) return "Mac" + wl[3].toUpperCase() + wl.slice(4); return wl[0].toUpperCase() + wl.slice(1); };
const titleCase = (name) => { if (!name) return name; const tokens = name.trim().split(/\s+/); const last = tokens.length - 1; return tokens.map((tok, i) => { const low = tok.toLowerCase(); const core = low.replace(/[^a-z]/g, ""); if (ACRONYMS.has(core)) return tok.toUpperCase(); if (SMALL.has(low) && i > 0 && i < last) return low; return tok.split(/([-/])/).map(p => (p === "-" || p === "/" ? p : capWord(p))).join(""); }).join(" "); };

const deduplicateResults = (results) => {
  const seen = new Map(); const out = [];
  for (const r of results) { const key = (r.facility || "").toLowerCase().trim() + "|" + r.payer; if (!seen.has(key)) { seen.set(key, true); out.push(r); } }
  return out;
};

const matchesExclusion = (facility, excludedKeys) => {
  if (!facility || excludedKeys.length === 0) return false;
  const name = facility.toLowerCase();
  return excludedKeys.some(key => (SPECIALTY_KEYWORDS[key] || []).some(kw => name.includes(kw)));
};

const filterAndSort = (results, tab, sortBy, excludedKeys) => {
  let filtered = results
    .filter(r => r.facility && !r.facility.startsWith("Provider "))
    .filter(r => !matchesExclusion(r.facility, excludedKeys));
  if (tab !== "all") filtered = filtered.filter(r => r.provider_category === tab);
  const copy = [...filtered];
  if (sortBy === "price") return copy.sort((a, b) => headlinePrice(a) - headlinePrice(b));
  if (sortBy === "type")  return copy.sort((a, b) => { if (a.provider_category === "Facility" && b.provider_category !== "Facility") return -1; if (b.provider_category === "Facility" && a.provider_category !== "Facility") return 1; return headlinePrice(a) - headlinePrice(b); });
  return copy;
};

// ZIP → city
const zipCityCache = {};
const getZipCity = async (zip) => {
  if (zipCityCache[zip]) return zipCityCache[zip];
  try { const r = await fetch(`https://api.zippopotam.us/us/${zip}`); if (r.ok) { const d = await r.json(); const city = d.places?.[0]?.["place name"] || zip; const state = d.places?.[0]?.["state abbreviation"] || ""; const label = city + (state ? ", " + state : ""); zipCityCache[zip] = label; return label; } } catch {}
  return zip;
};

const cityCoordCache = {};
const geocodeCity = async (input) => {
  const key = input.toLowerCase().trim();
  if (cityCoordCache[key]) return cityCoordCache[key];
  try {
    const q = encodeURIComponent(key + (key.includes(",") ? "" : ", USA"));
    const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=5&q=${q}&countrycodes=us&addressdetails=1`,
      { headers: { "User-Agent": "MioPrice/1.0 (mioprice.com)" } });
    if (r.ok) {
      const d = await r.json();
      if (d.length > 0) {
        // Prefer city/town results over neighborhoods
        const best = d.find(x => ["city","town","municipality"].includes(x.type) || ["city","town"].includes(x.addresstype)) || d[0];
        const city = best.address?.city || best.address?.town || best.address?.municipality || (best.display_name||"").split(",")[0];
        const state = best.address?.state_code || best.address?.state || "";
        const label = city.trim() + (state ? ", " + state.trim() : "");
        const result = { lat: parseFloat(best.lat), lng: parseFloat(best.lon), label };
        cityCoordCache[key] = result;
        return result;
      }
    }
  } catch {}
  return null;
};

const isZipCode = (s) => /^\d{5}$/.test(s.trim());

// Responsive hook
function useIsMobile() {
  const [mobile, setMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= 768);
  useEffect(() => { const h = () => setMobile(window.innerWidth <= 768); window.addEventListener("resize", h); return () => window.removeEventListener("resize", h); }, []);
  return mobile;
}

// ── Pipeline step ─────────────────────────────────────────────────────────────
function PipelineStep({ label, state, isLast }) {
  const done = state === "done", active = state === "active";
  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <div style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, whiteSpace: "nowrap", background: done ? "#f0fdf4" : active ? "#eff6ff" : "#f9fafb", border: `1px solid ${done ? "#bbf7d0" : active ? "#bfdbfe" : "#e5e7eb"}`, color: done ? "#16a34a" : active ? "#2563eb" : "#9ca3af", fontWeight: done || active ? 600 : 400, transition: "all 0.2s" }}>
        {done ? "✓ " : active ? "· " : "○ "}{label}
      </div>
      {!isLast && <div style={{ width: 14, height: 1, background: "#e5e7eb", margin: "0 2px" }} />}
    </div>
  );
}

// ── Facility card ─────────────────────────────────────────────────────────────
function FacilityCard({ result, rank, isExpanded, onToggle, allResults = [], isComparing = false, onCompare, compareDisabled = false, globalPayer = "all", onGlobalPayerChange }) {
  const vc       = VALUE_CONFIG[result.value_tier] || VALUE_CONFIG.fair;
  const isTop    = rank === 0;
  const isHosp   = result.type === "Medical Facility";
  const name     = titleCase(result.facility);
  const physFrom = lowestByClass(result.rates, "professional");
  const facFrom  = lowestByClass(result.rates, "institutional");
  const physRange = rangeByClass(result.rates, "professional");
  const facRange  = rangeByClass(result.rates, "institutional");
  const grouped  = groupRates(result.rates);
  const inds     = getValueIndicators(result, allResults);
  const payers   = [...new Set((result.rates || []).map(r => r.payer))];
  const selPayer    = globalPayer;
  const setSelPayer = onGlobalPayerChange || (() => {});

  return (
    <div onClick={onToggle} style={{ background: "#fff", borderRadius: 14, marginBottom: 10, overflow: "hidden", cursor: "pointer", border: `1.5px solid ${isExpanded ? "#2563eb" : isTop ? "#bfdbfe" : "#e5e7eb"}`, boxShadow: isTop ? "0 2px 12px rgba(37,99,235,0.09)" : "0 1px 3px rgba(0,0,0,0.04)", transition: "border-color 0.15s" }}>
      {isTop && (
        <div style={{ background: "#eff6ff", borderBottom: "1px solid #bfdbfe", padding: "5px 16px", display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#2563eb" }} />
          <span style={{ fontSize: 11, fontWeight: 600, color: "#1d4ed8" }}>Lowest rate for this procedure</span>
        </div>
      )}
      <div style={{ padding: "14px 16px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>


          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 3 }}>
              <span style={{ fontSize: 16, lineHeight: 1.4, flexShrink: 0 }}>{isHosp ? "🏥" : "👨‍⚕️"}</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#111827", lineHeight: 1.4 }}>{name}</span>
            </div>
            {/* Row 1: indicators + Google rating */}
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center", marginBottom: 4 }}>
              {inds.map((ind, idx) => (
                <span key={idx} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 10, background: ind.bg, color: ind.color, border: "1px solid " + ind.border, fontWeight: 600 }}>
                  {ind.icon} {ind.label}
                </span>
              ))}
              {result.google_rating && <span style={{ fontSize: 11, color: "#6b7280" }}>⭐ {result.google_rating} ({(result.google_reviews||0).toLocaleString()})</span>}
            </div>

            {/* Row 2: address + directions on one line */}
            {result.address && (
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6, display: "flex", gap: 6, alignItems: "center", minWidth: 0 }}>
                <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0, flex: 1 }}>
                  📍 {titleCase(result.address)}
                </span>
                <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(result.address)}`}
                   target="_blank" rel="noopener noreferrer"
                   onClick={e => e.stopPropagation()}
                   style={{ textDecoration: "none", flexShrink: 0, display: "inline-flex", alignItems: "center" }}
                   title="Get directions">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#ea4335"/>
                    <circle cx="12" cy="9" r="2.5" fill="#fff"/>
                  </svg>
                </a>
              </div>
            )}


          </div>

          <div style={{ textAlign: "right", flexShrink: 0 }}>
            {physRange && facRange ? (
              <>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#2a6f97", lineHeight: 1.2 }}>Physician {priceRange(physRange.lo, physRange.hi)}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1f7a52", lineHeight: 1.2, marginTop: 2 }}>Facility {priceRange(facRange.lo, facRange.hi)}</div>
                <div style={{ fontSize: 9.5, color: "#9ca3af", marginTop: 3 }}>both fees usually apply</div>
              </>
            ) : physRange ? (
              <><div style={{ fontSize: 22, fontWeight: 800, color: "#111827", lineHeight: 1 }}>{priceRange(physRange.lo, physRange.hi)}</div><div style={{ fontSize: 10, color: "#374151", marginTop: 2 }}>physician fee</div></>
            ) : facRange ? (
              <><div style={{ fontSize: 22, fontWeight: 800, color: "#111827", lineHeight: 1 }}>{priceRange(facRange.lo, facRange.hi)}</div><div style={{ fontSize: 10, color: "#374151", marginTop: 2 }}>facility fee</div></>
            ) : <div style={{ fontSize: 16, color: "#9ca3af" }}>—</div>}
            <div style={{ marginTop: 5, display: "inline-block", fontSize: 10, padding: "2px 8px", borderRadius: 10, background: vc.bg, color: vc.color, border: `1px solid ${vc.border}`, fontWeight: 600 }}>{vc.label}</div>

          </div>
        </div>

        {/* Rate chips with payer tabs */}
        {grouped.length > 0 && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #f3f4f6" }}>

            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              {/* Left: chip rows 90% */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {grouped.map(([code, rows]) => {
                  const filtered = (selPayer === "all" ? rows : rows.filter(r => r.payer === selPayer))
                    .filter(isDisplayable);
                  if (!filtered.length) return null;
                  return (
                    <div key={code} style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "#9ca3af", marginBottom: 5 }}>{serviceLabel(code)}</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {filtered.map((rt, i) => { const p = PAYER_COLORS[rt.payer] || { bg: "#f9fafb", color: "#374151", label: (rt.payer || "").toUpperCase() }; return (<div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 9px", borderRadius: 8, background: p.bg, border: `1px solid ${p.color}22` }}>{selPayer === "all" && <span style={{ fontSize: 11, fontWeight: 700, color: p.color }}>{p.label}</span>}<span style={{ fontSize: 10, color: "#6b7280" }}>{feeLabel(rt.billing_class)}</span><span style={{ fontSize: 12, fontWeight: 700, color: "#111827" }}>{priceRange(rt.min_rate, rt.max_rate)}</span></div>); })}
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Right: compare button 10% */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                <button
                  onMouseDown={e => { e.stopPropagation(); e.preventDefault(); onCompare && onCompare(); }}
                  onClick={e => e.stopPropagation()}
                  disabled={compareDisabled}
                  style={{ fontSize: 11, padding: "4px 10px", borderRadius: 20, border: `1.5px solid ${isComparing ? "#16a34a" : compareDisabled ? "#e5e7eb" : "#2563eb"}`, background: isComparing ? "#f0fdf4" : "#fff", color: isComparing ? "#16a34a" : compareDisabled ? "#9ca3af" : "#2563eb", cursor: compareDisabled ? "not-allowed" : "pointer", fontWeight: 600, whiteSpace: "nowrap" }}>
                  {isComparing ? "✓" : "+ Compare"}
                </button>
                <button
                  onMouseDown={e => { e.stopPropagation(); e.preventDefault(); onToggle && onToggle(); }}
                  onClick={e => e.stopPropagation()}
                  style={{ fontSize: 11, padding: "4px 10px", borderRadius: 20, border: `1.5px solid ${isExpanded ? "#16a34a" : "#e5e7eb"}`, background: isExpanded ? "#f0fdf4" : "#fff", color: isExpanded ? "#16a34a" : "#6b7280", cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap", marginTop: 4, display: "block" }}>
                  {isExpanded ? "✓ Community" : "👥 Community"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Expanded detail */}
        {isExpanded && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #f3f4f6" }}>
            <div style={{ display: "flex", gap: 8 }}>
              {[["NPI", result.npi, true], ["Network", result.network, false], ["Source", result.mrf_source || "TCR-MRF", false]].map(([l, v, mono]) => (
                <div key={l} style={{ background: "#f9fafb", borderRadius: 8, padding: "7px 12px", flex: 1, minWidth: 80 }}>
                  <div style={{ fontSize: 10, color: "#9ca3af", marginBottom: 2 }}>{l}</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: l === "Network" ? "#16a34a" : "#374151", fontFamily: mono ? "monospace" : "inherit" }}>{v}</div>
                </div>
              ))}
            </div>
            <CommunityReports
              npi={result.npi}
              cptCode={result.cpt}
              payer={selPayer === "all" ? null : selPayer}
              negotiated={facRange || physRange}
              apiBase={process.env.REACT_APP_API_URL || "https://api.mioprice.com"}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function HealthcareSearch({ embedded = false }) {
  const mobile = useIsMobile();

  const [query,         setQuery]         = useState("");
  const [zip,           setZip]           = useState("");
  const [cityCoords,    setCityCoords]    = useState(null);
  const [cityList,      setCityList]      = useState([]);
  const [citySugs,      setCitySugs]      = useState([]);
  const [showCitySug,   setShowCitySug]   = useState(false);
  const [locationLabel, setLocationLabel] = useState("");
  const [radius,        setRadius]        = useState(15);
  const [running,       setRunning]       = useState(false);
  const [allResults,    setAllResults]    = useState([]);
  const [displayed,     setDisplayed]     = useState(PAGE_SIZE);
  const [activeTab,     setActiveTab]     = useState("Facility");
  const [sortBy,        setSortBy]        = useState("price");
  const [expandedId,    setExpandedId]    = useState(null);
  const [compareList,   setCompareList]   = useState([]);  // max 3
  const [showCompare,   setShowCompare]   = useState(false);
  const [pipeState,     setPipeState]     = useState({});
  const [meta,          setMeta]          = useState(null);
  const [error,         setError]         = useState(null);
  const [excluded,      setExcluded]      = useState(["hearing","dental","vision","podiatry","optical"]);
  const [showRadiusDrop,setShowRadiusDrop]= useState(false);
  const [activeArea,    setActiveArea]    = useState(null);
  const [suggestions,   setSuggestions]   = useState([]);
  const [searchTerms,   setSearchTerms]   = useState(QUICK_SEARCHES_FALLBACK);
  const [showSug,       setShowSug]       = useState(false);
  const [priceCache,    setPriceCache]    = useState({});
  const [selPayer,      setSelPayer]      = useState("all");

  const loaderRef  = useRef(null);

  const availablePayers = useMemo(() => {
    try {
      const seen = new Set();
      (allResults || []).forEach(r => ((r && r.rates) || []).forEach(rt => rt && rt.payer && seen.add(rt.payer)));
      return [...seen].sort();
    } catch(e) { return []; }
  }, [allResults]);
  const inputRef   = useRef(null);
  const sugRef     = useRef(null);

  // Infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(entries => { if (entries[0].isIntersecting) setDisplayed(d => d + PAGE_SIZE); }, { threshold: 0.1 });
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [allResults]);

  // Close suggestion dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (sugRef.current && !sugRef.current.contains(e.target)) setShowSug(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Load search terms from API on mount
  useEffect(() => {
    const API = process.env.REACT_APP_API_URL || "https://api.mioprice.com";
    fetch(API + "/api/search-terms")
      .then(r => r.json())
      .then(d => {
        if (d.terms && d.terms.length > 0) {
          // Extract unique display terms, title-cased
          const tc = s => s.replace(/\b\w/g, c => c.toUpperCase());
          const unique = [...new Set(d.terms.map(t => tc(t.term)))];
          setSearchTerms(unique.sort());
        }
      })
      .catch(() => {}); // keep fallback on error
  }, []);

  // Load city list for location autocomplete
  useEffect(() => {
    const API = process.env.REACT_APP_API_URL || "https://api.mioprice.com";
    fetch(API + "/api/cities")
      .then(r => r.json())
      .then(d => { if (d.cities) setCityList(d.cities); })
      .catch(() => {});
  }, []);

  // Auto-detect location on mount
  useEffect(() => {
    if (zip) return;
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { latitude: lat, longitude: lng } = pos.coords;
        const nom = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
        if (nom.ok) {
          const d = await nom.json();
          const z = d.address?.postcode?.slice(0, 5);
          if (z && /^\d{5}$/.test(z)) {
            setZip(z);
            getZipCity(z).then(setLocationLabel);
          }
        }
      } catch {}
    }, () => {});
  }, []);

  // Suggestion filtering
  const handleQueryChange = (val) => {
    setQuery(val);
    if (!val.trim()) { setSuggestions([]); setShowSug(false); return; }
    const q = val.toLowerCase();
    const matches = searchTerms.filter(s => s.toLowerCase().includes(q)).slice(0, 8);
    setSuggestions(matches);
    setShowSug(matches.length > 0);
  };

  const selectSuggestion = (s) => {
    setQuery(s);
    setSuggestions([]);
    setShowSug(false);
    setTimeout(() => runSearch(s), 50);
  };

  const animatePipeline = useCallback(async () => {
    const delay = ms => new Promise(r => setTimeout(r, ms));
    for (const step of PIPELINE_STEPS) {
      setPipeState(p => ({ ...p, [step.id]: "active" }));
      await delay(500);
      setPipeState(p => ({ ...p, [step.id]: "done" }));
    }
  }, []);

  const toggleCompare = (result) => {
    setCompareList(prev => {
      const exists = prev.find(r => r.npi === result.npi);
      if (exists) return prev.filter(r => r.npi !== result.npi);
      if (prev.length >= 3) return prev; // max 3
      return [...prev, result];
    });
  };

  const runSearch = async (overrideQuery) => {
    const q = (overrideQuery || query).trim();
    const z = zip.trim();
    if (!q || running) return;
    setShowSug(false);
    setRunning(true); setAllResults([]); setDisplayed(PAGE_SIZE); setSelPayer("all");
    setExpandedId(null); setPipeState({}); setMeta(null); setError(null);
    animatePipeline();
    try {
      let params;
      if (!z) {
        setError("Please enter a location.");
        setRunning(false); return;
      } else if (isZipCode(z)) {
        getZipCity(z).then(setLocationLabel);
        setCityCoords(null);
        params = new URLSearchParams({ query: q, limit: 500, radius, zip: z });
      } else {
        const geo = await geocodeCity(z);
        if (!geo) {
          setError(`Could not find "${z}". Try a zip code instead.`);
          setRunning(false); return;
        }
        setLocationLabel(geo.label);
        setCityCoords(geo);
        params = new URLSearchParams({ query: q, limit: 500, radius, lat: geo.lat, lng: geo.lng, loc: geo.label });
      }
      const res  = await fetch(API_BASE + "?" + params.toString());
      const data = await res.json();
      if (data.error) {
        setError(data.error);
      } else {
        const deduped = deduplicateResults(data.results || []);
        setAllResults(deduped);
        const hospCount = deduped.filter(r => r.type === "Medical Facility").length;
        setMeta({ count: hospCount, elapsed: data.elapsed, cpt_code: data.cpt_code, description: data.description });
        // Cache best price for this query for suggestions
        const best = deduped.length ? Math.min(...deduped.map(headlinePrice).filter(Number.isFinite)) : null;
        if (best) setPriceCache(prev => ({ ...prev, [q.toLowerCase()]: best }));
      }
    } catch {
      setError("Failed to fetch results. Please try again.");
    } finally {
      setRunning(false);
    }
  };

  const switchTab = (tab) => { setActiveTab(tab); setDisplayed(PAGE_SIZE); setExpandedId(null); };

  const tabFiltered = filterAndSort(
    selPayer === "all" ? allResults
      : allResults.filter(r => (r.rates || []).some(rt => rt.payer === selPayer)),
    activeTab, sortBy, excluded
  );
  const visible     = tabFiltered.slice(0, displayed);
  const hasMore     = displayed < tabFiltered.length;
  const named       = filterAndSort(allResults, "all", "price", excluded);
  const hospitals   = named.filter(r => r.type === "Medical Facility").length;
  const individuals = named.filter(r => r.type !== "Medical Facility").length;
  const _prices     = named.map(headlinePrice).filter(Number.isFinite);
  const bestPrice   = _prices.length ? Math.min(..._prices) : 0;

  const px = mobile ? "14px" : "32px";

  return (
    <div style={{ background: embedded ? "transparent" : "#f8fafc", minHeight: embedded ? "auto" : "100vh", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", color: "#111827" }}>

      {/* NAV — standalone only */}
      {!embedded && (
        <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", padding: `10px ${px}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: mobile ? 20 : 22, fontWeight: 800, letterSpacing: -0.5 }}>
            <span style={{ color: "#111827" }}>Mio</span><span style={{ color: "#2563eb" }}>Price</span>
          </div>
          <button style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, padding: "7px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Sign in</button>
        </div>
      )}

      {/* ── SEARCH BAR ── */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", padding: mobile ? "12px 14px" : "16px 32px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          {/* One-line search: procedure + zip + radius + button */}
          <div ref={sugRef} style={{ position: "relative" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: mobile ? "wrap" : "nowrap" }}>
              {/* Procedure */}
              <div style={{ flex: mobile ? "1 1 100%" : 1, position: "relative" }}>
                <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 15, pointerEvents: "none" }}>🔍</span>
                <input
                  ref={inputRef}
                  value={query}
                  onChange={e => handleQueryChange(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") runSearch(); if (e.key === "Escape") setShowSug(false); }}
                  placeholder="MRI, Blood Work, Knee replacement, CT Scan..."
                  style={{ width: "100%", border: "1.5px solid #e5e7eb", borderRadius: 10, padding: "11px 12px 11px 36px", fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit", transition: "border-color 0.15s" }}
                  onFocus={e => { e.target.style.borderColor = "#2563eb"; if (suggestions.length) setShowSug(true); }}
                  onBlur={e  => { e.target.style.borderColor = "#e5e7eb"; }}
                />
              </div>

              {/* ZIP */}
              <div style={{ position: "relative", width: mobile ? 110 : 160, flexShrink: 0 }}>
                <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, pointerEvents: "none" }}>📍</span>
                <input
                  value={locationLabel || zip}
                  onChange={e => {
                    const v = e.target.value; setZip(v); setLocationLabel("");
                    if (v.length >= 2 && !isZipCode(v)) {
                      const matches = cityList.filter(c => c.toLowerCase().startsWith(v.toLowerCase())).slice(0, 6);
                      setCitySugs(matches); setShowCitySug(matches.length > 0);
                    } else { setCitySugs([]); setShowCitySug(false); }
                  }}
                  onKeyDown={e => e.key === "Enter" && runSearch()}
                  placeholder="ZIP or city"
                  style={{ width: "100%", border: "1.5px solid #e5e7eb", borderRadius: 10, padding: "11px 10px 11px 28px", fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
                  onFocus={e => { e.target.style.borderColor = "#2563eb"; if (locationLabel) setLocationLabel(""); }}
                  onBlur={e  => { e.target.style.borderColor = "#e5e7eb"; setTimeout(() => setShowCitySug(false), 150); if (zip && !locationLabel && /^\d{5}$/.test(zip.trim())) getZipCity(zip).then(setLocationLabel); }}
                />
              {/* City suggestions dropdown */}
              {showCitySug && citySugs.length > 0 && (
                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", zIndex: 100, overflow: "hidden" }}>
                  {citySugs.map((city, i) => (
                    <div key={city} onMouseDown={() => {
                      setZip(city); setLocationLabel(city); setCitySugs([]); setShowCitySug(false);
                    }} style={{ padding: "10px 16px", cursor: "pointer", fontSize: 14, borderBottom: i < citySugs.length-1 ? "1px solid #f3f4f6" : "none", display: "flex", alignItems: "center", gap: 8 }}>
                      <span>📍</span> {city}
                    </div>
                  ))}
                </div>
              )}
              </div>

              {/* Radius */}
              <div style={{ position: "relative", flexShrink: 0 }}>
                <button onClick={() => setShowRadiusDrop(d => !d)} style={{ border: "1.5px solid #e5e7eb", borderRadius: 10, padding: "11px 10px", background: "#fff", cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap", color: "#374151" }}>
                  {radius}mi <span style={{ fontSize: 9, color: "#9ca3af" }}>▼</span>
                </button>
                {showRadiusDrop && (
                  <div style={{ position: "absolute", top: "calc(100% + 4px)", right: 0, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 200, minWidth: 80, overflow: "hidden" }}>
                    {RADIUS_OPTIONS.map(r => (
                      <div key={r} onClick={() => { setRadius(r); setShowRadiusDrop(false); }} style={{ padding: "8px 14px", cursor: "pointer", fontSize: 13, background: radius === r ? "#eff6ff" : "#fff", color: radius === r ? "#2563eb" : "#374151", fontWeight: radius === r ? 600 : 400 }}>{r} mi</div>
                    ))}
                  </div>
                )}
              </div>

              {/* Search button */}
              <button onClick={() => runSearch()} disabled={running || !query.trim()} style={{ background: running || !query.trim() ? "#93c5fd" : "#2563eb", border: "none", borderRadius: 10, padding: "11px 18px", color: "#fff", fontSize: 13, fontWeight: 600, cursor: running || !query.trim() ? "not-allowed" : "pointer", whiteSpace: "pre", flexShrink: 0, lineHeight: 1.2, textAlign: "center" }}>
                {running ? "…" : "Get\nRates"}
              </button>
            </div>

            {/* Suggestion dropdown */}
            {showSug && suggestions.length > 0 && (
              <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 300, overflow: "hidden" }}>
                {suggestions.map((s, i) => {
                  const cached = priceCache[s.toLowerCase()];
                  return (
                    <div key={s} onMouseDown={() => selectSuggestion(s)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 16px", cursor: "pointer", borderBottom: i < suggestions.length - 1 ? "1px solid #f3f4f6" : "none", background: "#fff", transition: "background 0.1s" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                      onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 14, color: "#9ca3af" }}>🔍</span>
                        <span style={{ fontSize: 14, color: "#111827" }}>{s}</span>
                        {(() => { const area = PROCEDURE_AREA[s.toLowerCase()]; return area ? (
                          <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", marginLeft: 4 }}>
                            <span style={{ fontSize: 16, lineHeight: 1 }}>{area.icon}</span>
                            <span style={{ fontSize: 10, color: "#6b7280", whiteSpace: "nowrap" }}>{area.label}</span>
                          </span>
                        ) : null; })()}
                      </div>
                      {cached && (
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#16a34a" }}>from {fmt(cached)} near you</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* PIPELINE */}
      {running && (
        <div style={{ background: "#fff", borderBottom: "1px solid #f3f4f6", padding: `8px ${px}`, overflowX: "auto" }}>
          <div style={{ maxWidth: 900, margin: "0 auto", display: "flex", alignItems: "center" }}>
            {PIPELINE_STEPS.map((step, i) => (
              <PipelineStep key={step.id} label={step.label} state={pipeState[step.id] || "pending"} isLast={i === PIPELINE_STEPS.length - 1} />
            ))}
          </div>
        </div>
      )}

      {/* RESULTS */}
      <div style={{ maxWidth: 900, margin: "0 auto", padding: mobile ? "14px 14px" : "20px 32px" }}>

        {error && (
          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "14px 18px", marginBottom: 16, color: "#dc2626", fontSize: 13 }}>⚠️ {error}</div>
        )}

        {named.length > 0 && (
          <>
            {/* Meta line */}
            <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 12 }}>
              <strong style={{ color: "#111827" }}>{meta?.description}</strong>
              {zip ? ` — ${radius}mi from ${locationLabel || zip}` : " — IL statewide"}
              <span style={{ marginLeft: 10, color: "#9ca3af" }}>·</span>
              <span style={{ marginLeft: 6, fontWeight: 700, color: "#16a34a" }}>{named.length} facilities</span>
              <span style={{ marginLeft: 6, color: "#9ca3af" }}>· Rates from</span>
              <span style={{ marginLeft: 4, fontWeight: 700, color: "#111827" }}>{fmt(bestPrice)}</span>
              <span style={{ marginLeft: 6, fontSize: 11, color: "#9ca3af" }}>UHC · Aetna · Cigna · TCR-MRF</span>
            </div>

            {/* Global payer filter */}
            {availablePayers.length > 1 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                <button onClick={() => setSelPayer("all")}
                  style={{ fontSize: 12, padding: "4px 12px", borderRadius: 20,
                    border: `1.5px solid ${selPayer === "all" ? "#374151" : "#e5e7eb"}`,
                    background: selPayer === "all" ? "#111827" : "#fff",
                    color: selPayer === "all" ? "#fff" : "#6b7280",
                    cursor: "pointer", fontWeight: selPayer === "all" ? 600 : 400 }}>
                  All plans
                </button>
                {availablePayers.map(p => {
                  const pc = PAYER_COLORS[p] || { color: "#374151", bg: "#f3f4f6", label: p.toUpperCase() };
                  const active = selPayer === p;
                  return (
                    <button key={p} onClick={() => setSelPayer(p)}
                      style={{ fontSize: 12, padding: "4px 12px", borderRadius: 20,
                        border: `1.5px solid ${pc.color}`,
                        background: active ? pc.bg : "#fff",
                        color: active ? pc.color : "#6b7280",
                        cursor: "pointer", fontWeight: active ? 700 : 500 }}>
                      {pc.label}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Controls */}
            <div style={{ display: "flex", gap: 6, marginBottom: 12, alignItems: "center", flexWrap: "wrap" }}>
              {/* Tab filter */}
              <div style={{ display: "flex", gap: 4, background: "#f3f4f6", borderRadius: 8, padding: 3, overflowX: "auto", maxWidth: "100%", WebkitOverflowScrolling: "touch" }}>
                {TABS.map(tab => {
                  const count = tab.key === "all" ? named.length : named.filter(r => r.provider_category === tab.key).length;
                  const active = activeTab === tab.key;
                  return (
                    <button key={tab.key} onClick={() => switchTab(tab.key)} style={{ fontSize: 12, padding: "5px 12px", borderRadius: 6, border: "none", background: active ? "#fff" : "transparent", color: active ? "#2563eb" : "#6b7280", cursor: "pointer", fontWeight: active ? 600 : 400, boxShadow: active ? "0 1px 3px rgba(0,0,0,0.1)" : "none", whiteSpace: "nowrap", flexShrink: 0 }}>{(mobile ? tab.short : tab.label)} ({count})</button>
                  );
                })}
              </div>

            </div>

            {/* Cards */}
            {visible.map((r, i) => (
              <FacilityCard key={r.npi + r.payer + r.billing_class} result={r} rank={i}
                allResults={tabFiltered}
                isExpanded={expandedId === (r.npi + r.payer)}
                onToggle={() => setExpandedId(expandedId === (r.npi + r.payer) ? null : (r.npi + r.payer))}
                isComparing={compareList.some(c => c.npi === r.npi)}
                onCompare={() => toggleCompare(r)}
                compareDisabled={compareList.length >= 3 && !compareList.some(c => c.npi === r.npi)}
                globalPayer={selPayer}
                onGlobalPayerChange={setSelPayer} />
            ))}

            <div style={{ fontSize: 10, color: "#9ca3af", lineHeight: 1.6, padding: "8px 4px" }}>
              ⓘ Published negotiated rates per plan. Physician and facility fees are separate charges. These rates don't apply to cash-pay, Medicare, or Medicaid. Confirm costs with your provider before scheduling.
            </div>

            <div ref={loaderRef} style={{ padding: "16px 0", textAlign: "center" }}>
              {hasMore
                ? <span style={{ fontSize: 12, color: "#9ca3af" }}>↓ Scroll for more</span>
                : <span style={{ fontSize: 12, color: "#d1d5db" }}>All {tabFiltered.length} results shown</span>}
            </div>

            <div style={{ marginTop: 8, padding: "12px 16px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 10 }}>
              <p style={{ fontSize: 11, color: "#9ca3af", lineHeight: 1.7, margin: 0 }}>
                <strong style={{ color: "#6b7280" }}>ⓘ About this data:</strong> Rates sourced from public Transparency in Coverage Rule (TCR) machine-readable files. Your actual cost varies by deductible and plan design. Confirm with your provider before scheduling.
              </p>
            </div>
          </>
        )}

      {/* Sticky compare bar */}
      {compareList.length > 0 && !showCompare && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 500, background: "#fff", borderTop: "2px solid #2563eb", padding: "10px 16px", display: "flex", alignItems: "center", gap: 8, boxShadow: "0 -4px 16px rgba(0,0,0,0.1)" }}>
          <div style={{ display: "flex", gap: 6, flex: 1, overflowX: "auto" }}>
            {compareList.map(r => (
              <div key={r.npi} style={{ display: "flex", alignItems: "center", gap: 5, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "4px 8px", whiteSpace: "nowrap", flexShrink: 0 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#1d4ed8", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis" }}>{titleCase(r.facility).split(" ").slice(0,2).join(" ")}</span>
                <span style={{ fontSize: 12, color: "#16a34a", fontWeight: 700 }}>{fmt(headlinePrice(r))}</span>
                <button onClick={() => toggleCompare(r)} style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: 14, padding: 0, lineHeight: 1 }}>×</button>
              </div>
            ))}
          </div>
          <button onClick={() => setShowCompare(true)} style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", flexShrink: 0 }}>
            Compare {compareList.length}
          </button>
        </div>
      )}

      {/* Compare modal */}
      {showCompare && compareList.length > 0 && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }} onClick={() => setShowCompare(false)}>
          <div style={{ background: "#fff", width: "100%", maxWidth: 600, maxHeight: "60vh", borderRadius: 16, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>Compare Facilities</div>
              <button onClick={() => setShowCompare(false)} style={{ background: "#f3f4f6", border: "none", borderRadius: 8, padding: "6px 14px", fontSize: 13, cursor: "pointer" }}>Done</button>
            </div>

            {/* Comparison table — scrollable */}
            <div style={{ overflowY: "auto", overflowX: "auto", flex: 1 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 320 }}>
                {/* Facility names */}
                <thead>
                  <tr>
                    <td style={{ padding: "10px 12px", fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", width: 110, borderBottom: "1px solid #f3f4f6" }}>Facility</td>
                    {compareList.map(r => (
                      <td key={r.npi} style={{ padding: "10px 10px", borderBottom: "1px solid #f3f4f6", verticalAlign: "top" }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#111827", marginBottom: 2 }}>{titleCase(r.facility)}</div>
                        <div style={{ fontSize: 11, color: "#6b7280" }}>{r.city}</div>
                        <button onClick={() => { toggleCompare(r); if (compareList.length <= 1) setShowCompare(false); }} style={{ marginTop: 4, fontSize: 10, color: "#9ca3af", background: "none", border: "none", cursor: "pointer", padding: 0 }}>Remove</button>
                      </td>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Physician fee */}
                  <tr style={{ background: "#f9fafb" }}>
                    <td style={{ padding: "10px 12px", fontSize: 11, color: "#6b7280", fontWeight: 600 }}>Physician fee</td>
                    {compareList.map(r => {
                      const profRates = (r.rates || []).filter(rt => rt.billing_class === "professional");
                      if (!profRates.length) return <td key={r.npi} style={{ padding: "10px 10px", fontSize: 14, fontWeight: 700, color: "#9ca3af" }}>—</td>;
                      const min = Math.min(...profRates.map(rt => rt.min_rate));
                      const max = Math.max(...profRates.map(rt => rt.max_rate || rt.min_rate));
                      return <td key={r.npi} style={{ padding: "10px 10px", fontSize: 14, fontWeight: 800, color: "#16a34a" }}>{min === max ? fmt(min) : `${fmt(min)}–${fmt(max)}`}</td>;
                    })}
                  </tr>
                  {/* Facility fee */}
                  <tr>
                    <td style={{ padding: "10px 12px", fontSize: 11, color: "#6b7280", fontWeight: 600 }}>Facility fee</td>
                    {compareList.map(r => {
                      const instRates = (r.rates || []).filter(rt => rt.billing_class === "institutional");
                      if (!instRates.length) return <td key={r.npi} style={{ padding: "10px 10px", fontSize: 14, fontWeight: 700, color: "#9ca3af" }}>—</td>;
                      const min = Math.min(...instRates.map(rt => rt.min_rate));
                      const max = Math.max(...instRates.map(rt => rt.max_rate || rt.min_rate));
                      return <td key={r.npi} style={{ padding: "10px 10px", fontSize: 14, fontWeight: 700, color: "#374151" }}>{min === max ? fmt(min) : `${fmt(min)}–${fmt(max)}`}</td>;
                    })}
                  </tr>
                  {/* Google rating */}
                  <tr style={{ background: "#f9fafb" }}>
                    <td style={{ padding: "10px 12px", fontSize: 11, color: "#6b7280", fontWeight: 600 }}>Google rating</td>
                    {compareList.map(r => (
                      <td key={r.npi} style={{ padding: "10px 10px", fontSize: 13, color: "#374151" }}>
                        {r.google_rating ? `⭐ ${r.google_rating} (${(r.google_reviews||0).toLocaleString()})` : "—"}
                      </td>
                    ))}
                  </tr>
                  {/* CMS rating */}
                  <tr>
                    <td style={{ padding: "10px 12px", fontSize: 11, color: "#6b7280", fontWeight: 600 }}>CMS rating</td>
                    {compareList.map(r => (
                      <td key={r.npi} style={{ padding: "10px 10px", fontSize: 13, color: "#374151" }}>
                        {r.cms_star_rating ? `${"★".repeat(r.cms_star_rating)} ${r.cms_star_rating}/5` : "—"}
                      </td>
                    ))}
                  </tr>
                  {/* Payers */}
                  <tr style={{ background: "#f9fafb" }}>
                    <td style={{ padding: "10px 12px", fontSize: 11, color: "#6b7280", fontWeight: 600 }}>Payers</td>
                    {compareList.map(r => {
                      const payers = [...new Set((r.rates||[]).map(rt => rt.payer))];
                      return (
                        <td key={r.npi} style={{ padding: "10px 10px" }}>
                          {payers.map(p => {
                            const pc = PAYER_COLORS[p] || { color: "#374151", bg: "#f9fafb", label: p.toUpperCase() };
                            return <span key={p} style={{ fontSize: 10, padding: "2px 6px", borderRadius: 6, background: pc.bg, color: pc.color, fontWeight: 600, marginRight: 4, display: "inline-block", marginBottom: 3 }}>{pc.label}</span>;
                          })}
                        </td>
                      );
                    })}
                  </tr>
                  {/* Directions */}
                  <tr>
                    <td style={{ padding: "10px 12px", fontSize: 11, color: "#6b7280", fontWeight: 600 }}>Directions</td>
                    {compareList.map(r => (
                      <td key={r.npi} style={{ padding: "10px 10px" }}>
                        {r.address ? <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(r.address)}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }} title="Get directions"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#ea4335"/><circle cx="12" cy="9" r="2.5" fill="#fff"/></svg></a> : "—"}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{ padding: "16px", background: "#f9fafb", margin: 16, borderRadius: 10 }}>
              <p style={{ fontSize: 11, color: "#9ca3af", lineHeight: 1.7, margin: 0 }}>
                <strong style={{ color: "#6b7280" }}>ⓘ</strong> Rates are published negotiated rates per plan. Physician and facility fees are separate charges. These rates don't apply to cash-pay, Medicare, or Medicaid. Confirm with your provider before scheduling.
              </p>
            </div>


          </div>
        </div>
      )}


        {/* Empty state */}
        {!named.length && !running && !error && (
          <div style={{ textAlign: "center", padding: "56px 0 40px" }}>
            <div style={{ fontSize: 42, marginBottom: 14 }}>🏥</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#111827", marginBottom: 8 }}>Find the best rate for your care</div>
            <div style={{ fontSize: 14, color: "#9ca3af", lineHeight: 1.6, marginBottom: 28, maxWidth: 380, margin: "0 auto 28px" }}>
              Real negotiated rates from federal Transparency in Coverage data — before you even book.
            </div>
            <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 10 }}>Popular searches</div>
            <div style={{ overflowX: "auto", msOverflowStyle: "none", scrollbarWidth: "none", marginLeft: -14, marginRight: -14, paddingLeft: 14, paddingRight: 14, marginBottom: 10 }}>
              <style>{`.hs-strip::-webkit-scrollbar{display:none}`}</style>
              <div className="hs-strip" style={{ display: "flex", gap: 8, width: "max-content", paddingBottom: 2 }}>
                {searchTerms.slice(0, 14).map(q => (
                  <button key={q} onMouseDown={() => { setQuery(q); setTimeout(() => runSearch(q), 50); }} style={{ fontSize: 13, padding: "7px 16px", borderRadius: 20, border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", cursor: "pointer", fontWeight: 500, whiteSpace: "nowrap" }}>{q}</button>
                ))}
              </div>
            </div>
            <div style={{ overflowX: "auto", msOverflowStyle: "none", scrollbarWidth: "none", marginLeft: -14, marginRight: -14, paddingLeft: 14, paddingRight: 14 }}>
              <div className="hs-strip" style={{ display: "flex", gap: 8, width: "max-content", paddingBottom: 2 }}>
                {searchTerms.slice(14, 28).map(q => (
                  <button key={q} onMouseDown={() => { setQuery(q); setTimeout(() => runSearch(q), 50); }} style={{ fontSize: 13, padding: "7px 16px", borderRadius: 20, border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", cursor: "pointer", fontWeight: 500, whiteSpace: "nowrap" }}>{q}</button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
